package ro.horatiu_udrea.resursedeblocat

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import resursedeblocat.composeapp.generated.resources.*

import androidx.compose.foundation.clickable
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.ui.draw.alpha
import androidx.compose.foundation.rememberScrollState
import androidx.compose.ui.draw.clip
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import coil3.compose.rememberAsyncImagePainter

@Composable
fun ResourceListScreen(
    resources: List<ResourceDetails>,
    isLoading: Boolean,
    onFindNewResource: () -> Unit,
    onResourceClick: (ResourceDetails) -> Unit,
    onLogout: () -> Unit
) {
    val scrollState = rememberScrollState()

    LaunchedEffect(resources.size) {
        scrollState.animateScrollTo(0)
    }

    BaseScreenLayout(
        scrollState = scrollState,
        footer = {
            Spacer(modifier = Modifier.height(16.dp))
            TextButton(
                onClick = onLogout,
                modifier = Modifier.padding(bottom = 8.dp)
            ) {
                Text(
                    text = stringResource(Res.string.logout),
                    style = MaterialTheme.typography.labelLarge,
                )
            }
        }
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)) {
            val isWide = maxWidth > 500.dp
            val content = @Composable { modifier: Modifier ->
                Column(
                    modifier = modifier.padding(horizontal = 12.dp),
                    horizontalAlignment = if (isWide) Alignment.Start else Alignment.CenterHorizontally
                ) {
                    Text(
                        text = stringResource(Res.string.my_resources),
                        style = MaterialTheme.typography.headlineMedium,
                        color = MaterialTheme.colorScheme.secondary
                    )
                }
            }
            val button = @Composable { modifier: Modifier ->
                Button(
                    onClick = onFindNewResource,
                    shape = MaterialTheme.shapes.medium,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ),
                    modifier = modifier
                ) {
                    Text(stringResource(Res.string.find_new_resources), style = MaterialTheme.typography.labelLarge)
                }
            }

            if (isWide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    content(Modifier)
                    button(Modifier)
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    content(Modifier.fillMaxWidth())
                    button(Modifier.fillMaxWidth())
                }
            }
        }

        if (isLoading) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        } else if (resources.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                Text(
                    text = stringResource(Res.string.no_resources),
                    style = MaterialTheme.typography.bodyLarge,
                    color = GeneralText
                )
            }
        } else {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                resources.forEach { resource ->
                    ResourceCard(resource = resource, onClick = { onResourceClick(resource) })
                }
            }
        }
    }
}

@Composable
fun ResourceCard(resource: ResourceDetails, onClick: () -> Unit) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .clickable(onClick = onClick),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(12.dp),
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier
                    .size(60.dp)
                    .clip(RoundedCornerShape(8.dp)),
                color = ThemePrimary.copy(alpha = 0.1f)
            ) {
                val isObtained = resource.state != ResourceState.FRESH && resource.state != ResourceState.NOT_OBTAINED
                
                if (!resource.imagine.isNullOrBlank()) {
                    Image(
                        painter = rememberAsyncImagePainter(model = resource.imagine),
                        contentDescription = null,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    Image(
                        painter = painterResource(Res.drawable.lock),
                        contentDescription = null,
                        modifier = Modifier.padding(8.dp),
                        contentScale = ContentScale.Fit
                    )
                }
            }

            Spacer(modifier = Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text(
                        text = resource.tip,
                        style = MaterialTheme.typography.labelSmall,
                        color = ThemePrimary,
                        modifier = Modifier
                            .background(ThemePrimary.copy(alpha = 0.1f), RoundedCornerShape(4.dp))
                            .padding(horizontal = 6.dp, vertical = 2.dp)
                    )
                    StateChip(state = resource.state)
                }
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = resource.nume,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = ThemeTextDark
                )
                Text(
                    text = resource.autor,
                    style = MaterialTheme.typography.bodySmall,
                    color = GeneralText.copy(alpha = 0.7f)
                )
            }

            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                contentDescription = null,
                tint = ThemePrimary.copy(alpha = 0.5f)
            )
        }
    }
}
